import * as React from "react"
import MaterialTable, {Column} from "material-table"
import {IconButton, Switch, Typography, Tooltip, Grid} from "@material-ui/core"
import SearchIcon from "@material-ui/icons/Search"
import PlayArrowIcon from "@material-ui/icons/PlayArrow"
import StopIcon from "@material-ui/icons/Stop"
import {
    SSOLicenseCount,
    SsoTeacherClass
} from "../../../../types/types"
import {LicenseSelect} from "./LicenseSelect"
import {ChangeEvent, RefObject, useState} from "react"
import {Person} from "@material-ui/icons"
import {CustomTeacherView} from "./dialogs/CustomTeacherView"
import productPackageStore from "../../../common/ProductPackage/ProductPackageStore";

interface ClassListProps {
    classList: SsoTeacherClass[],
    tableRef: RefObject<MaterialTable<SsoTeacherClass>>,
    viewReports: (event: any, row:any) => void,
    startContinueTest: (event: any, row:any) => void,
    stopTest: (event: any, row:any) => void,
    handleProductChange: (event: React.ChangeEvent<{ name?: string; value: unknown }>, row: SsoTeacherClass, tableId: number) => void,
    toggleHandwritten: (event: ChangeEvent<HTMLInputElement>, row: SsoTeacherClass, tableId: number) => void,
    updateHandwritten: (row: SsoTeacherClass, newValue: boolean, tableId: number) => void,
    isWritingInPackage: (packageVal?: string | number) => boolean
    licenses_sold: SSOLicenseCount[],
    handleSelectionChange: (rows: SsoTeacherClass[]) => void
}

function getStatus(rowData: SsoTeacherClass) {
    if (rowData.logins === null) {
        return rowData.status
    }
    return rowData.logins.allowTest ? "TESTING_READY" : "TESTING_PAUSED"
}

export const ClassList: React.FC<ClassListProps> = (props) => {


    // This useMemo was introduced to solve a subtle bug.
    // Selecting a checkbox would cause the parent to update, then re-rendering this classList.
    // Material Table would use the old object reference to mark the checkbox, but then this map function wiped it out.
    // The classList should be mapped once when it is loaded, and not again unless the whole list is refreshed.
    // TODO - This mapping should be moved to the backend so the frontend gets a clean list in the format it needs.
    const classList = React.useMemo(() => (
            props.classList?.map?.((row: SsoTeacherClass) =>
                ({...row, status: getStatus(row)}))
            ?? []),
        [props.classList]
    )

    const [teacherOpen, setTeacherOpen] = useState(false)
    const [selectLoginId, setSelectLoginId] = useState(-1)

    const handleCustomTeacherViewClose = () => {
        setTeacherOpen(false)
    }
   React.useEffect(()=>{
     productPackageStore.initialize()
   },[])
    const selectionProps = (rowData: SsoTeacherClass) => {
        return {
            disabled: rowData.logins !== null,
            color: "secondary"
        }
    }
    const isAllowHidden = (rowData: SsoTeacherClass): boolean => {
        if (rowData.logins !== null && rowData.logins.allowTest === false) { return false }
        return true
    }
    const isDisallowHidden = (rowData: SsoTeacherClass): boolean => {
        if (rowData.logins !== null && rowData.logins.allowTest === true) { return false }
        return true
    }
    const isViewReportHidden = (rowData: SsoTeacherClass): boolean => {
        return (rowData.logins === undefined || rowData.logins === null || !rowData.logins.testCode)
    }

    const statusLookup = {
        "NEEDS_DISTRICT_SETUP": "Needs Product Reservations",
        "DISTRICT_SETUP_NEED_CLASSROOM_SETUP": "Needs Class Setup",
        "TESTING_READY": "Testing Ready",
        "TESTING_PAUSED": "Testing Paused"
    }

    const handleProductChange = (e:ChangeEvent<{name?:string, value:unknown}>, rowData: any, index: number): void => {
        // Update the value in the state variable, so that it renders correctly.
        rowData.productName  = e.target.value

        // Send the new value to the parent to be saved.
        props.handleProductChange(e, rowData, index)

        // If a non-Writing package is selected...
        if (props.isWritingInPackage(rowData.productName) === false) {
            // Send the new value (false) to the parent to be saved.
            props.updateHandwritten(rowData, false, rowData.tableData.id)
        }
    }

    const productDropdown = (rowData: any): React.ReactFragment => {
        if (rowData.logins && rowData.productName) {
            return (<>{productPackageStore.productPackages.find((pp) => pp.code === rowData.productName)?.longName || productPackageStore.productPackageToName[rowData.productName]}</>)
        } else {
            if (rowData.licenses_sold) {
                return (
                    <LicenseSelect
                        rowData={rowData}
                        tableId={rowData.tableData.id}
                        selectedPackage={rowData.productName}
                        handleChange={handleProductChange}
                        licenses_sold={rowData.licenses_sold}
                    />
                )
            } else {
                return (<Typography>No Licenses available</Typography>)
            }
        }
    }

    const handwrittenControl = (rowData: any): React.ReactFragment => {
        if (rowData.logins) {
            if (rowData.logins.handwritten) {
                return (<>Using Handwritten</>)
            } else {
                return (<></>)
            }
        } else {
            return (
                <Switch
                    onChange={(e) => {
                        if (props.isWritingInPackage(rowData.productName)) {
                            // Update the value in the state variable, so that it renders correctly.
                            rowData.handwritten = e.target.checked

                            // Send the new value to the parent to be saved.
                            props.toggleHandwritten(e, rowData, rowData.tableData.id)
                        }
                    }}
                    checked={rowData.handwritten}
                    disabled={rowData.isHandwrittenSold === false || props.isWritingInPackage(rowData.productName) === false}
                    inputProps={{"aria-label": "Use handwritten form for written parts of the test."}}
                    color={"primary"}
                />
            )
        }
    }

    const testCode = (rowData: any): React.ReactFragment => {
        return <>
            {rowData.logins && <>{rowData.logins.testCode}</>}
        </>
    }

    const actions = (rowData: any): React.ReactFragment => {
        return <Grid container spacing={0}>
            {!isViewReportHidden(rowData) && <Grid item={true} xs={6}><Tooltip title={"View Test Results"}>
                <IconButton onClick={
                    (event) => props.viewReports(event, rowData)
                }>
                    <SearchIcon />
                </IconButton>
            </Tooltip></Grid>}
            {!isAllowHidden(rowData) && <Grid item={true} xs={6}><Tooltip title={"Allow Students to take the test"}>
                <IconButton onClick={
                    (event) => props.startContinueTest(event, rowData)
                }>
                    <PlayArrowIcon />
                </IconButton>
            </Tooltip></Grid>}
            {!isDisallowHidden(rowData) && <Grid item={true} xs={6}><Tooltip title={"Disallow Students to take the test"}>
                <IconButton onClick={
                    (event) => props.stopTest(event, rowData)
                }>
                    <StopIcon />
                </IconButton>
            </Tooltip></Grid>}
            <Grid item={true} xs={6}>
            <Tooltip title={"View Teacher for this Class"}>
                <IconButton onClick={
                    (event) => {
                        setSelectLoginId(rowData.logins.id)
                        setTeacherOpen(true)
                    }
                }>
                    <Person />
                </IconButton>
            </Tooltip>
            </Grid>
        </Grid>
    }

    // Previously columns was state, but with no setState method invoked.
    // It was believed that Material Table needs columns to be state or it would have issues with filtering and sorting.
    // This does not seem to be the case. If we are not changing the state of the columns array then it is not really state.
    // There are various state management issues here to unravel but making columns state was not helping with filtering, sorting or re-rendering.
    const columns: Array<Column<SsoTeacherClass>> = [
        {title: "Class", field: "name"},
        {title: "Course Id", field: "courseId"},
        {
            title: "School",
            field: "schoolName",
            defaultSort: "asc",
            customSort: (a, b) => a.schoolName?.localeCompare?.(b.schoolName ?? "") ?? 0
        },
        {title: "Class Count", field: "count"},
        // To be restored later (see AVANT-2762):
        //{title: "Consumed", field: "countConsumed"},
        {title: "Est. Testing Date", field: "estTestingDate", type: "date"},
        {title: "Product", field: "productName", render: productDropdown},
        {title: "Language", field: "language"},
        {title: "Handwritten", field: "handwritten", render: handwrittenControl, filtering: false},
        {title: "Test Code", field: "logins.testCode", render: testCode},
        {title: "Status", field: "status", lookup: statusLookup},
        {title: "Actions", field: "actions", render: actions, filtering: false},
    ]

    return (
        <>
        <MaterialTable
            tableRef={props.tableRef}
            title="Class List"
            columns={columns}
            data={classList}
            options={{
                selection: true,
                filtering: true,
                search: false,
                sorting: true,
                showTitle: true,
                toolbar: true,
                pageSize: 50,
                pageSizeOptions: [5,10,20,50,100],
                padding: "dense",
                selectionProps: selectionProps,
                thirdSortClick: false,
            }}
            onSelectionChange={props.handleSelectionChange}
        />
            <CustomTeacherView
                open={teacherOpen}
                onClose={handleCustomTeacherViewClose}
                loginId={selectLoginId}
            />
        </>
    )
}
